<template>
  <div class="tw-flex tw-bg-[#FFF]">
    <div
      class="tw-w-[100%] tw-p-[20px] tw-min-h-[73px] tw-border tw-border-solid tw-rounded-s-md tw-border-[#EFF0F1]"
    >
      <div
        class="tw-flex tw-items-center tw-justify-between tw-mb-[13px] tw-w-[100%] tw-h-[20px]"
      >
        <div
          class="tw-text-[15px] tw-font-[600] tw-leading-[130%] tw-text-[#211F58]"
        >
          {{ getComponentUiText("app-scan-scanning-scan-status-1") }}
        </div>
        <div
          v-if="isRefreshing && webServicesId === webserviceScanningId"
          class="custom-global-skeleton tw-w-[100px] tw-h-[23px] tw-rounded-[4px] tw-text-[11px] tw-font-[500] tw-leading-[130%] tw-cursor-pointer tw-px-[5px] tw-py-[3px] ${statusStyle}"
        />
        <div
          v-else-if="isRefreshing && domainId === domainScanningId"
          class="custom-global-skeleton tw-w-[100px] tw-h-[23px] tw-rounded-[4px] tw-text-[11px] tw-font-[500] tw-leading-[130%] tw-cursor-pointer tw-px-[5px] tw-py-[3px] ${statusStyle}"
        />
        <div
          v-else
          :class="`tw-rounded-[4px] tw-text-[11px] tw-font-[500] tw-leading-[130%] tw-cursor-pointer tw-px-[5px] tw-py-[3px] ${statusStyle}`"
        >
          {{ currentScanStatus }}
        </div>
      </div>
      <div class="tw-h-[40px]">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-[6px]">
          <span
            class="tw-text-[13px] tw-font-[500] tw-leading-[130%] tw-text-[#211F58]"
            >{{ getComponentUiText("app-scan-scanning-scan-status-started-1") }}
          </span>
          <span
            v-if="isRefreshing && webServicesId === webserviceScanningId"
            class="custom-global-skeleton tw-h-[17px] tw-w-[100px]"
          />
          <span
            v-else-if="isRefreshing && domainId === domainScanningId"
            class="custom-global-skeleton tw-h-[17px] tw-w-[100px]"
          />
          <span
            v-else
            class="tw-text-[13px] tw-font-[400] tw-text-[#878787] tw-leading-[130%]"
          >
            {{ getStartedTime(data.startedAt) }}</span
          >
        </div>
        <div class="tw-flex tw-items-center tw-justify-between">
          <span
            class="tw-text-[13px] tw-font-[500] tw-leading-[130%] tw-text-[#211F58]"
            >{{ getComponentUiText("app-scan-scanning-scan-elapsed-time-1") }}
          </span>
          <span
            v-if="isRefreshing && webServicesId === webserviceScanningId"
            class="custom-global-skeleton tw-h-[17px] tw-w-[100px]"
          />
          <span
            v-else-if="isRefreshing && domainId === domainScanningId"
            class="custom-global-skeleton tw-h-[17px] tw-w-[100px]"
          />
          <span
            v-else
            class="tw-text-[13px] tw-font-[400] tw-text-[#878787] tw-leading-[130%]"
            >{{ getElapsedTime(data.startedAt, data.endedAt) }}
          </span>
        </div>
      </div>
    </div>

    <div
      style="
        border-top: 1px solid #eff0f1;
        border-right: 1px solid #eff0f1;
        border-bottom: 1px solid #eff0f1;
      "
      class="tw-p-[20px] tw-bg-[#FFF] tw-max-h-[114px] tw-max-w-[114px] tw-rounded-r-md tw-border-[#EFF0F1]"
    >
      <RadialProgressBar
        class="d-inline"
        :total-steps="100"
        :completed-steps="percentProgress"
        :is-hide-progress="true"
        outer-stroke-color="#48A0FF"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getComponentUiText, getElapsedTime } from "@/utils/utilities";
import RadialProgressBar from "../prospect/prospectQuestions/componentTypes/radialProgressBar.vue";
import { computed, PropType } from "vue";
import { getTimeFromNow } from "@/utils/dateTimeUtils";
import type { ScansHistoryWebServicesType } from "@/store/scanning";
import store from "@/store";
import dayjs from "dayjs";

const props = defineProps({
  percentProgress: {
    type: Number,
    required: false,
    default: 20,
  },
  data: {
    type: Object as PropType<
      ScansHistoryWebServicesType["scanReportRuns"][number]
    >,
    required: true,
  },
  webServicesId: {
    type: String,
    required: true,
  },
  domainId: {
    type: String,
    required: true,
  },
});

const statusStyle = computed(() => {
  return props.data?.runStatusId;
});

const currentScanStatus = computed(() => {
  return props.data?.runStatus;
});

const isRefreshing = computed(() => store.state.Scanning.isLoading);
const webserviceScanningId = computed(
  () => store.state.Scanning.webserviceScanningId,
);
const domainScanningId = computed<string>(
  () => store.state.Scanning.domainScanningId,
);

const getStartedTime = (time: string) => {
  return getTimeFromNow(time);
};
</script>

<style lang="scss" scoped>
.inProgress,
.inprog {
  background: linear-gradient(
      0deg,
      rgba(72, 160, 255, 0.15) 0%,
      rgba(72, 160, 255, 0.15) 100%
    ),
    #fff;
  color: #387dff;
}

.defunct {
  background: #fff;
  border: 1px solid var(--ui-orange-soft, #fee5e2);
  color: #f7553c;
}

.complete,
.ok {
  background: #fff;
  border: 1px solid var(--ui-green-soft, #d9ede2);
  color: #01893d;
}

.audited {
  background: #fff;
  border: 1px solid #eee4f9;
  color: #904bd6;
}

.onHold {
  background: #fff;
  border: 1px solid #fff0da;
  color: #fe9a05;
}

.notStarted,
.err {
  background: #fff;
  border: 1px solid #fee5e2;
  color: #f7553c;
}
</style>
